import React, { createContext, useContext, useMemo, useRef } from 'react';
import _ from 'lodash';
import Quill from 'quill/core';
import type { Facet } from './quillFacets';
import { getSeparator } from './quillFacets';
import deltaToText from '../editor/utils/deltaToText';

type SearchBarContextApi = {
  onSearchBarEditorMounted(quill: Quill): void;
  appendFacet(facet: Facet): void;
};

const SearchBarContext = createContext<SearchBarContextApi | null>(null);

interface SearchBarContextProviderProps {
  children: React.ReactNode;
}

export function appendFacet(quill: Quill, facet: Facet) {
  const content = quill.getContents();
  const lastOp = _.last(content.ops);

  const lastOpText = typeof lastOp?.insert === 'string' ? lastOp.insert : '';
  const prefixWithSeparator = `${facet.prefix}${getSeparator(facet)}`;

  const matches = lastOpText.matchAll(new RegExp(prefixWithSeparator, 'g'));
  const match = _.last([...matches]);

  if (match) {
    const offset = lastOpText.length - (match.index ?? 0);
    quill.focus();
    quill.setSelection({
      index: quill.getLength() - offset + prefixWithSeparator.length,
      length: offset - prefixWithSeparator.length,
    });
    return;
  }

  const contentText = deltaToText(content);
  const value =
    contentText.trimEnd().length > 0
      ? ` ${prefixWithSeparator}`
      : prefixWithSeparator;

  quill.insertText(quill.getLength() - 1, value, Quill.sources.USER);
  quill.focus();
  quill.setSelection({
    index: quill.getLength(),
    length: 0,
  });
}

export const SearchBarContextProvider = ({
  children,
}: SearchBarContextProviderProps) => {
  const quillRef = useRef<Quill | null>(null);

  const context = useMemo<SearchBarContextApi>(() => {
    return {
      onSearchBarEditorMounted(quill) {
        quillRef.current = quill;
      },
      appendFacet(facet) {
        if (quillRef.current) {
          appendFacet(quillRef.current, facet);
        }
      },
    };
  }, []);

  return (
    <SearchBarContext.Provider value={context}>
      {children}
    </SearchBarContext.Provider>
  );
};

export const useSearchBar = () => {
  const value = useContext(SearchBarContext);
  if (!value) {
    throw new Error(
      'useSearchBar must be used within a SearchBarContextProvider',
    );
  }
  return value;
};
